import type { Components } from '@/api/generated/api';
import { BillingMode } from '@/api/generated/api.enum';
import * as billingApi from '@/api/resources/billing.api';
import { useAnalytics } from '@/composables/analytics/useAnalytics';
import { useCurrentTenant } from '@/composables/schoolAdmin/useCurrentTenant';
import { usePermissions } from '@/composables/usePermissions';
import { useToast } from '@/composables/useToast';
import { AnalyticsEvents } from '@/services/analytics/events';
import type { BillingInterval } from '@/utils/billing';
import { toValue, type MaybeRefOrGetter } from 'vue';
import { useRoute } from 'vue-router';

/** Explicit user-triggered reads (previews, documents and portal links); nothing loads on setup. */
export function useBillingRequests(canManageBilling: MaybeRefOrGetter<boolean>) {
  const { catchError } = useToast();
  const { currentTenant } = useCurrentTenant();
  const { hasTenantOwnerAccess } = usePermissions();
  const { trackEvent } = useAnalytics();
  const route = useRoute();
  const read = <T>(request: () => Promise<T>): Promise<T | null> =>
    toValue(canManageBilling) ? catchError(request) : Promise.resolve(null);
  const getTenantPortalLink = async (): Promise<string | null> => {
    if (!hasTenantOwnerAccess.value) return null;
    return catchError(async () => {
      const url = await billingApi.fetchTenantPortalLink(route.fullPath);
      trackEvent(AnalyticsEvents.billing.portalOpened);
      return url;
    });
  };
  return {
    previewTenantCheckout: (
      productId: number,
      opts?: {
        quantity?: number;
        source?: Components.Schemas.CheckoutSource;
        billingInterval?: BillingInterval;
      },
    ) => read(() => billingApi.previewTenantCheckout({ productTypeId: productId, ...opts })),
    previewTenantPlanSwitch: (productId: number, opts?: { quantity?: number; billingInterval?: BillingInterval }) =>
      read(() => billingApi.previewTenantPlanSwitch({ productTypeId: productId, ...opts })),
    getTenantInvoiceAddress: () => read(billingApi.fetchTenantInvoiceAddress),
    downloadTenantOfferPdf: (offerId: number) => read(() => billingApi.fetchTenantOfferPdf(offerId)),
    getTenantPortalLink,
    getEntityPortalLink: () =>
      currentTenant.value == null || currentTenant.value.billingMode === BillingMode.billedExternally
        ? Promise.resolve(null)
        : getTenantPortalLink(),
  };
}
